import { createClient } from '@/lib/supabase/client';
import { queryCache } from '@/lib/supabase/queryCache';
import { subscriptionBatcher } from '@/lib/supabase/subscriptionBatcher';

export type NotificationType =
  | 'duty_assigned' |'duty_updated' |'duty_cancelled' |'leave_submitted' |'leave_approved' |'leave_rejected' |'violation_recorded' |'crew_added' |'crew_status_changed' |'shift_conflict' |'license_expiring' |'route_plan_created';

export type NotificationSeverity = 'critical' | 'warning' | 'info';

export interface AppNotification {
  id: string;
  type: NotificationType;
  severity: NotificationSeverity;
  title: string;
  detail: string;
  meta?: string;
  originScreen: string;
  originHref: string;
  originLabel: string;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

function mapRow(row: any): AppNotification {
  return {
    id: row.id,
    type: row.type as NotificationType,
    severity: row.severity as NotificationSeverity,
    title: row.title,
    detail: row.detail ?? '',
    meta: row.meta ?? undefined,
    originScreen: row.origin_screen ?? '',
    originHref: row.origin_href ?? '/',
    originLabel: row.origin_label ?? 'View',
    isRead: row.is_read ?? false,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const CACHE_KEY = 'notifications';

export const notificationService = {
  async getAll(): Promise<AppNotification[]> {
    return queryCache.dedupe(CACHE_KEY, async () => {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('notifications')
        .select('id,type,severity,title,detail,meta,origin_screen,origin_href,origin_label,is_read,created_at,updated_at')
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) { console.error('notificationService.getAll:', error.message); return []; }
      const result = (data ?? []).map(mapRow);
      queryCache.set(CACHE_KEY, result);
      return result;
    });
  },

  async getUnreadCount(): Promise<number> {
    const supabase = createClient();
    const { count, error } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('is_read', false);
    if (error) { console.error('notificationService.getUnreadCount:', error.message); return 0; }
    return count ?? 0;
  },

  async markRead(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', id);
    if (error) { console.error('notificationService.markRead:', error.message); return false; }
    queryCache.invalidate(CACHE_KEY);
    return true;
  },

  async markAllRead(): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('is_read', false);
    if (error) { console.error('notificationService.markAllRead:', error.message); return false; }
    queryCache.invalidate(CACHE_KEY);
    return true;
  },

  async deleteNotification(id: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', id);
    if (error) { console.error('notificationService.delete:', error.message); return false; }
    queryCache.invalidate(CACHE_KEY);
    return true;
  },

  /** Manually insert a notification (for client-side triggers if needed) */
  async create(notif: Omit<AppNotification, 'id' | 'createdAt' | 'updatedAt' | 'isRead'>): Promise<AppNotification | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('notifications')
      .insert({
        type: notif.type,
        severity: notif.severity,
        title: notif.title,
        detail: notif.detail,
        meta: notif.meta ?? null,
        origin_screen: notif.originScreen,
        origin_href: notif.originHref,
        origin_label: notif.originLabel,
      })
      .select()
      .single();
    if (error) { console.error('notificationService.create:', error.message); return null; }
    queryCache.invalidate(CACHE_KEY);
    return mapRow(data);
  },

  subscribeToChanges(callback: (notifications: AppNotification[]) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('notifications_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, () => {
        queryCache.invalidate(CACHE_KEY);
        subscriptionBatcher.schedule(
          CACHE_KEY,
          () => notificationService.getAll(),
          callback
        );
      })
      .subscribe();
    return () => {
      subscriptionBatcher.cancel(CACHE_KEY);
      supabase.removeChannel(channel);
    };
  },
};
